import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
  field: string
  direction: SortDirection
}

export interface SortColumnDef<T> {
  id: string
  /** Accessor that returns the value to sort by for a given item */
  accessor: (item: T) => string | number | null | undefined
}

export interface UseSortReturn<T> {
  /** The data after sorting */
  sortedData: T[]
  /** Current sort field and direction */
  sort: SortConfig
  /** Toggle sort on a column (cycles: asc -> desc -> default) */
  toggleSort: (field: string) => void
  /** Check if a column is the active sort column */
  isSorted: (field: string) => SortDirection | false
}

/**
 * A reusable hook for sorting data with URL-synced state.
 *
 * @param data - The source data array to sort (typically already filtered)
 * @param columns - Sort column definitions with accessors
 * @param defaultSort - Default sort configuration when no URL params are set
 */
export function useSort<T>(data: T[], columns: SortColumnDef<T>[], defaultSort: SortConfig): UseSortReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse sort state from URL, falling back to default
  const sort = useMemo<SortConfig>(() => {
    const field = searchParams.get('sort')
    const dir = searchParams.get('dir')
    if (field && columns.some((c) => c.id === field)) {
      return { field, direction: dir === 'asc' || dir === 'desc' ? dir : defaultSort.direction }
    }
    return defaultSort
  }, [searchParams, columns, defaultSort])

  // Sort the data
  const sortedData = useMemo(() => {
    const column = columns.find((c) => c.id === sort.field)
    if (!column) return data

    return [...data].sort((a, b) => {
      const aVal = column.accessor(a)
      const bVal = column.accessor(b)

      // Nulls always sort last
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      let cmp: number
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' })
      }

      return sort.direction === 'desc' ? -cmp : cmp
    })
  }, [data, columns, sort])

  const toggleSort = useCallback(
    (field: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const currentField = next.get('sort') || defaultSort.field
          const currentDir = next.get('dir') || defaultSort.direction

          if (currentField === field) {
            // Clicking same column: flip direction
            const newDir = currentDir === 'asc' ? 'desc' : 'asc'
            next.set('sort', field)
            next.set('dir', newDir)
          } else {
            // Clicking new column: set it with default direction
            next.set('sort', field)
            // For text columns default asc, for dates/numbers default desc
            const col = columns.find((c) => c.id === field)
            if (col) {
              next.set('dir', 'asc')
            }
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams, columns, defaultSort]
  )

  const isSorted = useCallback(
    (field: string): SortDirection | false => {
      return sort.field === field ? sort.direction : false
    },
    [sort]
  )

  return { sortedData, sort, toggleSort, isSorted }
}
